import {
  AudioOutlined,
  CheckOutlined,
  RobotOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Flex,
  FloatButton,
  Input,
  List,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { apiClient } from '@/api/client';
import VoiceRecorder from '@/pages/m6-crm/components/VoiceRecorder';

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;

type CommandStatus = 'PLANNED' | 'NEEDS_INPUT' | 'EXECUTING' | 'SUCCEEDED' | 'FAILED';

interface CommandAmbiguity {
  field?: string;
  message?: string;
  candidates?: Array<Record<string, unknown>>;
}

interface AssistantCommand {
  id: string;
  intent: string;
  risk: 'READ_ONLY' | 'CONFIRM_ONCE';
  status: CommandStatus;
  sourceType: 'TEXT' | 'VOICE';
  sourceText: string;
  missingFieldsJson: string[];
  ambiguitiesJson: CommandAmbiguity[];
  preview: string;
  requiresConfirmation: boolean;
  errorMessage?: string | null;
  resultJson?: {
    summary?: string;
    resourceUrl?: string;
    totalOnHand?: number;
    totalAvailable?: number;
    locations?: Array<Record<string, unknown>>;
  };
}

const INTENT_LABELS: Record<string, string> = {
  'assistant.capabilities.query': '助手能力',
  'inventory.stock.query': '库存查询',
  'inventory.receipt.createDraft': '创建入库草稿',
  'crm.customer.create': '新增客户',
  'crm.inquiry.create': '新增询盘',
  'crm.followUp.create': '新增跟进记录',
  'crm.task.create': '新增任务',
  'supplier.createDraft': '新增供应商草稿',
};

const FIELD_LABELS: Record<string, string> = {
  productName: '产品名称或型号',
  productNameOrModel: '产品名称或型号',
  quantity: '数量',
  customerId: '客户',
  contactName: '联系人',
  companyName: '公司名称',
  email: '邮箱',
  message: '询盘内容',
  content: '沟通内容',
  title: '任务标题',
  dueDate: '任务截止时间',
  priority: '任务优先级',
  contactedAt: '联系时间',
  nextFollowUpAt: '下次跟进时间',
  requiredDeliveryDate: '要求交期',
  companyNameEn: '公司英文名称（请按正式资料填写，不自动翻译）',
  companyNameZh: '公司中文名称',
  contactEmail: '联系邮箱',
  bankRoutingCode: '银行联行号',
  website: '公司网站',
};

const DISMISSED_COMMAND_STORAGE_KEY = 'luna-assistant-dismissed-command-id';

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as {
      message?: string | string[];
      error?: { message?: string };
    } | undefined;
    if (Array.isArray(data?.message)) return data.message.join('；');
    return data?.message || data?.error?.message || error.message;
  }
  return error instanceof Error ? error.message : '操作失败，请稍后重试';
}

function speak(text: string): void {
  if (!text || !('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'zh-CN';
  utterance.rate = 1;
  window.speechSynthesis.speak(utterance);
}

function valueText(value: unknown): string {
  if (value == null) return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export const LunaAssistant: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [planning, setPlanning] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [plan, setPlan] = useState<AssistantCommand | null>(null);
  const [voiceOpen, setVoiceOpen] = useState(false);

  useEffect(() => {
    if (!open || plan || planning || executing) return undefined;
    let cancelled = false;
    void apiClient
      .get<{ data: AssistantCommand | null }>('/assistant/commands/latest')
      .then((response) => {
        const latest = response.data.data;
        const dismissedId = window.localStorage.getItem(DISMISSED_COMMAND_STORAGE_KEY);
        if (!cancelled && latest && latest.id !== dismissedId) setPlan(latest);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [executing, open, plan, planning]);

  useEffect(() => {
    if (
      !open
      || !plan
      || !['PLANNED', 'NEEDS_INPUT', 'EXECUTING'].includes(plan.status)
    ) {
      return undefined;
    }
    let cancelled = false;
    const refresh = async (): Promise<void> => {
      try {
        const response = await apiClient.get<{ data: AssistantCommand }>(
          `/assistant/commands/${plan.id}`,
        );
        const next = response.data.data;
        if (cancelled) return;
        if (
          next.status === 'SUCCEEDED'
          && plan.status !== 'SUCCEEDED'
          && !['assistant.capabilities.query', 'inventory.stock.query'].includes(next.intent)
        ) {
          await queryClient.invalidateQueries();
          window.dispatchEvent(new CustomEvent('luna:data-changed', {
            detail: { intent: next.intent, result: next.resultJson },
          }));
        }
        setPlan(next);
      } catch {
        // Keep the last known auditable state; explicit actions re-check on the server.
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [open, plan?.id, plan?.status, queryClient]);

  const context = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const pathBin = location.pathname.match(/\/(?:bin|bins)\/([^/]+)/i)?.[1];
    const customerId = location.pathname.match(
      /^\/m6\/customers\/([0-9a-f-]{36})(?:\/|$)/i,
    )?.[1];
    return {
      page: `${location.pathname}${location.search}`,
      binCode: params.get('binCode') || pathBin || undefined,
      locale: navigator.language || 'zh-CN',
      entityType: customerId ? 'customer' : undefined,
      entityId: customerId,
    };
  }, [location.pathname, location.search]);

  const executePlan = useCallback(async (
    command: AssistantCommand,
    confirmed: boolean,
  ): Promise<void> => {
    if (executing) return;
    setExecuting(true);
    try {
      const response = await apiClient.post<{ data: AssistantCommand }>(
        `/assistant/commands/${command.id}/execute`,
        { confirmed },
      );
      const completed = response.data.data;
      setPlan(completed);
      if (completed.status === 'FAILED') {
        message.error(completed.errorMessage || '操作未完成，系统已回滚，请核对后重新生成计划');
        return;
      }
      if (completed.status !== 'SUCCEEDED') {
        throw new Error('命令状态尚未完成，请刷新后核对，勿重复提交');
      }
      const summary = completed.resultJson?.summary || '操作已完成';
      if (!['assistant.capabilities.query', 'inventory.stock.query'].includes(
        completed.intent,
      )) {
        await queryClient.invalidateQueries();
        window.dispatchEvent(new CustomEvent('luna:data-changed', {
          detail: {
            intent: completed.intent,
            result: completed.resultJson,
          },
        }));
      }
      message.success(summary);
      speak(summary);
    } catch (error: unknown) {
      message.error(getErrorMessage(error));
    } finally {
      setExecuting(false);
    }
  }, [executing, queryClient]);

  const submit = useCallback(async (
    inputText: string,
    sourceType: 'TEXT' | 'VOICE' = 'TEXT',
  ): Promise<void> => {
    if (planning || executing) return;
    const trimmed = inputText.trim();
    if (!trimmed) return;

    if (/^(确认|确定|执行|confirm)$/i.test(trimmed) && plan?.requiresConfirmation) {
      await executePlan(plan, true);
      setText('');
      return;
    }

    const combinedText = plan?.status === 'NEEDS_INPUT'
      ? `${plan.sourceText}\n补充信息：${trimmed}`
      : trimmed;
    setPlanning(true);
    try {
      const response = await apiClient.post<{ data: AssistantCommand }>(
        '/assistant/commands/plan',
        {
          text: combinedText,
          sourceType,
          context,
        },
      );
      const command = response.data.data;
      window.localStorage.removeItem(DISMISSED_COMMAND_STORAGE_KEY);
      setPlan(command);
      setText('');
      if (sourceType === 'VOICE') speak(command.preview);
      if (
        !command.requiresConfirmation
        && command.missingFieldsJson.length === 0
        && command.ambiguitiesJson.length === 0
      ) {
        await executePlan(command, false);
      }
    } catch (error: unknown) {
      message.error(getErrorMessage(error));
    } finally {
      setPlanning(false);
    }
  }, [context, executePlan, executing, plan, planning]);

  const handleVoiceText = useCallback((transcript: string): void => {
    setVoiceOpen(false);
    void submit(transcript, 'VOICE');
  }, [submit]);

  const result = plan?.resultJson;
  const locations = Array.isArray(result?.locations) ? result.locations : [];

  return (
    <>
      <FloatButton
        icon={<RobotOutlined />}
        type="primary"
        tooltip="LUNA AI 助手"
        onClick={() => setOpen(true)}
        style={{
          right: 'max(16px, env(safe-area-inset-right))',
          bottom: 'calc(72px + env(safe-area-inset-bottom))',
        }}
      />
      <Drawer
        title={(
          <Space>
            <RobotOutlined style={{ color: '#1677ff' }} />
            <span>LUNA AI 业务助手</span>
          </Space>
        )}
        width="min(520px, 100vw)"
        open={open}
        onClose={() => setOpen(false)}
        destroyOnClose={false}
        styles={{
          body: {
            paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
          },
        }}
      >
        <Paragraph type="secondary">
          可粘贴或说出客户、供应商、询盘、跟进和任务资料，也可询问“你能做什么”。写操作先预览、再确认且只执行一次；库存不会自动过账。
        </Paragraph>

        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            value={text}
            onChange={(event) => setText(event.target.value)}
            onPressEnter={(event) => {
              if (!event.shiftKey) {
                event.preventDefault();
                void submit(text);
              }
            }}
            autoSize={{ minRows: 3, maxRows: 7 }}
            placeholder={
              plan?.status === 'NEEDS_INPUT'
                ? '补充缺失信息，例如：型号是 EASY301-0808TN'
                : '例如：录入中太公司的询盘；或记录今天的客户电话跟进……'
            }
            disabled={planning || executing}
          />
          <Button
            type="primary"
            icon={planning ? <Spin size="small" /> : <SendOutlined />}
            onClick={() => void submit(text)}
            disabled={!text.trim() || planning || executing}
            style={{ height: 'auto' }}
          />
        </Space.Compact>

        <Flex gap={8} style={{ marginTop: 10 }}>
          <Button
            icon={<AudioOutlined />}
            onClick={() => setVoiceOpen((value) => !value)}
            disabled={planning || executing}
          >
            {voiceOpen ? '收起语音' : '按语音输入'}
          </Button>
          {plan && (
            <Button
              onClick={() => {
                if (plan) {
                  window.localStorage.setItem(DISMISSED_COMMAND_STORAGE_KEY, plan.id);
                }
                setPlan(null);
                setText('');
              }}
              disabled={planning || executing}
            >
              新命令
            </Button>
          )}
        </Flex>

        {voiceOpen && (
          <Card size="small" style={{ marginTop: 10 }}>
            <VoiceRecorder
              onRecordingComplete={() => undefined}
              onTranscriptionComplete={handleVoiceText}
              autoTranscribe
              preferBrowserRecognition
              disabled={planning || executing}
            />
          </Card>
        )}

        {plan && (
          <Card
            size="small"
            style={{ marginTop: 16 }}
            title={(
              <Space wrap>
                <Tag color="blue">{INTENT_LABELS[plan.intent] || plan.intent}</Tag>
                <Tag color={plan.risk === 'READ_ONLY' ? 'green' : 'orange'}>
                  {plan.risk === 'READ_ONLY' ? '只读' : '确认后执行'}
                </Tag>
                <Tag>{plan.status}</Tag>
              </Space>
            )}
          >
            <Title level={5} style={{ marginTop: 0 }}>操作预览</Title>

            {plan.status === 'PLANNED'
              && plan.risk === 'READ_ONLY'
              && !plan.requiresConfirmation
              && plan.missingFieldsJson.length === 0
              && plan.ambiguitiesJson.length === 0 && (
                <Alert
                  type="info"
                  showIcon
                  message="只读查询尚未执行。点击后仅查询数据，不会写入或修改业务记录。"
                  action={(
                    <Button
                      type="primary"
                      loading={executing}
                      disabled={executing}
                      onClick={() => void executePlan(plan, false)}
                    >
                      执行查询
                    </Button>
                  )}
                  style={{ marginBottom: 12 }}
                />
              )}
            <Paragraph>{plan.preview}</Paragraph>

            {plan.missingFieldsJson.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`请一次补充：${plan.missingFieldsJson.map((field) => FIELD_LABELS[field] || field).join('、')}`}
                style={{ marginBottom: 12 }}
              />
            )}

            {plan.ambiguitiesJson.map((ambiguity, index) => (
              <Alert
                key={`${ambiguity.field ?? 'ambiguity'}-${index}`}
                type="warning"
                showIcon
                message={ambiguity.message || '存在歧义，请明确选择'}
                description={(
                  <Space wrap style={{ marginTop: 6 }}>
                    {(ambiguity.candidates ?? []).map((candidate, candidateIndex) => {
                      const label = [
                        candidate['brand'],
                        candidate['modelNumber'],
                        candidate['code'],
                        candidate['name'],
                      ].filter(Boolean).join(' ');
                      return (
                        <Button
                          key={String(candidate['id'] ?? candidateIndex)}
                          size="small"
                          onClick={() => setText(`我选择 ${label}`)}
                        >
                          {label || `选项 ${candidateIndex + 1}`}
                        </Button>
                      );
                    })}
                  </Space>
                )}
                style={{ marginBottom: 12 }}
              />
            ))}

            {plan.status === 'PLANNED'
              && plan.requiresConfirmation
              && plan.missingFieldsJson.length === 0
              && plan.ambiguitiesJson.length === 0 && (
                <Alert
                  type="info"
                  showIcon
                  message="请核对客户、内容、日期、型号、数量、金额和联系方式。点击确认，或直接说“确认”。"
                  action={(
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      loading={executing}
                      disabled={executing}
                      onClick={() => void executePlan(plan, true)}
                    >
                      确认执行
                    </Button>
                  )}
                  style={{ marginBottom: 12 }}
                />
              )}

            {plan.status === 'SUCCEEDED' && result?.summary && (
              <Alert
                type="success"
                showIcon
                message={result.summary}
                action={result.resourceUrl ? (
                  <Button
                    size="small"
                    onClick={() => {
                      navigate(result.resourceUrl!);
                      setOpen(false);
                    }}
                  >
                    打开记录
                  </Button>
                ) : undefined}
                style={{ marginBottom: 12 }}
              />
            )}

            {locations.length > 0 && (
              <List
                size="small"
                header={(
                  <Text strong>
                    库位分布：在库 {result?.totalOnHand ?? 0}，可用 {result?.totalAvailable ?? 0}
                  </Text>
                )}
                dataSource={locations}
                renderItem={(item) => (
                  <List.Item>
                    <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
                      <Descriptions.Item label="产品">
                        {[item['brand'], item['modelNumber'], item['productName']].filter(Boolean).join(' ')}
                      </Descriptions.Item>
                      <Descriptions.Item label="仓库">
                        {valueText(item['warehouseCode'] || item['warehouseName'])}
                      </Descriptions.Item>
                      <Descriptions.Item label="库位">{valueText(item['binCode'])}</Descriptions.Item>
                      <Descriptions.Item label="可用">{valueText(item['qtyAvailable'])}</Descriptions.Item>
                    </Descriptions>
                  </List.Item>
                )}
              />
            )}
          </Card>
        )}
      </Drawer>
    </>
  );
};

export default LunaAssistant;
